// Spaces section of the workspace-scope sidebar — extracted from
// workspace-content.tsx during the P5 split to keep each section under the
// 400-LOC ceiling. Pure render: every input is a prop.

import { Fragment } from "react";
import { Separator } from "@/components/ui/separator";
import { EmptyState } from "@/components/ui/empty-state";
import { ChannelItem, type ChannelActions } from "../ChannelItem";
import { SectionHeaderPill } from "./section-header-pill";
import { SidebarSectionToggle } from "./sidebar-section-toggle";
import { SpaceRow } from "./SpaceRow";
import { useSpaceActions } from "./use-space-actions";

type SectionFilter = "all" | "recent" | "unread";

type CreateChannelContext =
  | { spaceId: string; spaceName: string; hostWorkspaceId?: string }
  | "workspace"
  | null;

interface MembershipLike {
  id: string;
  channel_id: string;
  notify?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyChannel = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnySpace = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type WorkspaceLike = any;

interface WorkspaceSpacesSectionProps {
  selectedWorkspace: string | null;
  isWorkspaceAdmin: boolean;

  clientSpaces: AnySpace[];
  displaySpaces: AnySpace[];
  spacesOpen: boolean;
  setSpacesOpen: React.Dispatch<React.SetStateAction<boolean>>;
  spacesFilter: SectionFilter;
  setSpacesFilter: React.Dispatch<React.SetStateAction<SectionFilter>>;
  setShowCreateClientSpace: (open: boolean) => void;
  expandedSpaces: Record<string, boolean>;
  toggleSpaceExpanded: (spaceId: string) => void;
  spaceChannelMap: Map<string, AnyChannel[]>;
  setCreateChannelContext: (ctx: CreateChannelContext) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setInviteContext: (ctx: any) => void;
  setSelectedClientSpace: (spaceId: string | null) => void;
  currentWorkspace: WorkspaceLike;
  selectedChannel: string | null;
  isUnread: (chId: string) => boolean;
  mutedSet: Set<string>;
  myMembers: MembershipLike[];
  getMentionCount: (chId: string) => number;
  canManageChannel: (channelId: string) => boolean;
  canDeleteChannel: boolean;
  channelsWithCollaborators: Set<string>;
  channelActions: ChannelActions;
}

export function WorkspaceSpacesSection({
  selectedWorkspace,
  isWorkspaceAdmin,
  clientSpaces,
  displaySpaces,
  spacesOpen,
  setSpacesOpen,
  spacesFilter,
  setSpacesFilter,
  setShowCreateClientSpace,
  expandedSpaces,
  toggleSpaceExpanded,
  spaceChannelMap,
  setCreateChannelContext,
  setInviteContext,
  setSelectedClientSpace,
  currentWorkspace,
  selectedChannel,
  isUnread,
  mutedSet,
  myMembers,
  getMentionCount,
  canManageChannel,
  canDeleteChannel,
  channelsWithCollaborators,
  channelActions,
}: WorkspaceSpacesSectionProps) {
  // Stable SpaceActions for memoized SpaceRow. The hook funnels live
  // deps through a ref so the returned object's identity never
  // changes — SpaceRow.memo bails out cleanly on Sidebar re-renders.
  const spaceActions = useSpaceActions({
    toggleSpaceExpanded,
    setCreateChannelContext,
    setInviteContext,
    setSelectedClientSpace,
    selectedWorkspace,
    currentWorkspace,
  });

  if (!selectedWorkspace) return null;
  if (!(isWorkspaceAdmin || clientSpaces.length > 0)) return null;

  return (
    <>
      <Separator className="mt-3" />
      <div className="px-2 pt-3 group/section">
        <div className="flex items-center px-2 mb-1">
          <SidebarSectionToggle
            label="Spaces"
            open={spacesOpen}
            onToggle={() => setSpacesOpen((v) => !v)}
            labelDataAttr
          />
          <SectionHeaderPill
            filter={spacesFilter}
            onFilterChange={setSpacesFilter}
            onCreateClick={
              isWorkspaceAdmin
                ? () => setShowCreateClientSpace(true)
                : undefined
            }
          />
        </div>
        {spacesOpen && (
          <div className="space-y-0.5">
            {displaySpaces.map((space: AnySpace) => {
              const isExpanded = expandedSpaces[space.id] ?? false;
              const spaceChannels = spaceChannelMap.get(space.id) ?? [];
              return (
                <Fragment key={space.id}>
                  <SpaceRow
                    space={space}
                    isExpanded={isExpanded}
                    isWorkspaceAdmin={isWorkspaceAdmin}
                    hasWorkspaceContext={!!selectedWorkspace}
                    actions={spaceActions}
                  />
                  {isExpanded && spaceChannels.length > 0 && (
                    <div className="space-y-0.5">
                      {spaceChannels.map((channel: AnyChannel) => (
                        <ChannelItem
                          key={channel.id}
                          channel={channel}
                          isSelected={selectedChannel === channel.id}
                          isUnread={isUnread(channel.id)}
                          isMuted={mutedSet.has(channel.id)}
                          mentionCount={getMentionCount(channel.id)}
                          membership={myMembers.find(
                            (m) => m.channel_id === channel.id,
                          )}
                          canManage={canManageChannel(channel.id)}
                          canDelete={canDeleteChannel}
                          hasCollaborators={channelsWithCollaborators.has(
                            channel.id,
                          )}
                          actions={channelActions}
                        />
                      ))}
                    </div>
                  )}
                </Fragment>
              );
            })}
            {displaySpaces.length === 0 && (
              <EmptyState layout="inset" title="No spaces yet" />
            )}
          </div>
        )}
      </div>
    </>
  );
}
